import { FC, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import { toast } from 'react-toastify';

import { STATES_WITH_EDITABLE_SHIPPING_ADDRESS } from '../../orders/models/OrderModel';
import {
  changeShippingAddress,
  listSaleableProducts,
  listShippingServices,
  setProduct,
  updateOrder,
} from '../../orders/services/OrderCRUD';
import {
  QuickSaleAddressModel,
  QuickSaleOrderModel,
} from '../models/QuickSaleModel';
import { apiErrorMessage, orderLinesSummary } from '../utils/quickSaleFormat';
import { formatAddress } from './CustomerCard';

// Saleable products only exist for these two stores (see AddProductModal).
const FR_STORE_ID = 6;
const ES_STORE_ID = 1;

type Option = { value: number; label: string };

type Props = {
  order: QuickSaleOrderModel;
  storeId: number;
  addresses: QuickSaleAddressModel[];
  linesEditable: boolean;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
};

/**
 * The order changes an agent makes on the phone: lines and extras, address,
 * carrier and delivery date. Each one is saved right away, like on the order
 * page, because it acts on that order alone.
 */
const OrderEditModal: FC<Props> = ({
  order,
  storeId,
  addresses,
  linesEditable,
  onClose,
  onChanged,
}) => {
  const intl = useIntl();
  const [products, setProducts] = useState<Option[]>([]);
  const [carriers, setCarriers] = useState<Option[]>([]);
  const [productToAdd, setProductToAdd] = useState<Option | null>(null);
  const [unitsToAdd, setUnitsToAdd] = useState(1);
  const [addressId, setAddressId] = useState<number | ''>('');
  const [carrierId, setCarrierId] = useState<number | ''>(
    order.shipping_service || ''
  );
  const [promiseDate, setPromiseDate] = useState(order.promise_date || '');
  const [busy, setBusy] = useState(false);
  const addressEditable = STATES_WITH_EDITABLE_SHIPPING_ADDRESS.includes(
    order.state as any
  );
  const productLines = (order.lines || []).filter(
    (line) => line.kind === 'product' && line.product?.id
  );

  useEffect(() => {
    const productStore = storeId === FR_STORE_ID ? FR_STORE_ID : ES_STORE_ID;
    listSaleableProducts(productStore)
      .then(({ data }) =>
        setProducts(
          data
            .filter((product) => !!product.price)
            .map((product) => ({
              value: product.id,
              label: `${product.name} - ${product.price}€`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      )
      .catch((error) => console.warn(error));
    listShippingServices()
      .then(({ data }) =>
        setCarriers(
          data.map((carrier: any) => ({
            value: carrier.id,
            label: carrier.name,
          }))
        )
      )
      .catch((error) => console.warn(error));
  }, [storeId]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
      toast.success(intl.formatMessage({ id: 'quickSale.saved' }));
      await onChanged();
    } catch (error) {
      toast.error(
        apiErrorMessage(
          error,
          intl.formatMessage({ id: 'quickSale.genericError' })
        )
      );
    } finally {
      setBusy(false);
    }
  }

  function setUnits(line, units: number) {
    run(() =>
      setProduct(
        order.id,
        line.product.id,
        Math.max(units, 0),
        line.fixed_unit_price_inc_tax === 0
      )
    );
  }

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <h4 className="mb-0">
          {intl.formatMessage(
            { id: 'quickSale.editOrderTitle' },
            { code: order.code }
          )}
        </h4>
      </Modal.Header>
      <Modal.Body>
        <h5 className="mb-3">
          {intl.formatMessage({ id: 'quickSale.lines' })}
        </h5>
        {!linesEditable && (
          <div className="text-muted mb-3">
            {intl.formatMessage({ id: 'quickSale.linesLocked' })}
          </div>
        )}
        <table className="table table-row-dashed align-middle gy-2 mb-3">
          <tbody>
            {productLines.map((line) => (
              <tr key={line.product.id}>
                <td>{orderLinesSummary([line])[0]}</td>
                <td className="text-end text-nowrap">
                  <button
                    type="button"
                    className="btn btn-icon btn-sm btn-light me-1"
                    disabled={!linesEditable || busy}
                    title={intl.formatMessage({ id: 'quickSale.decrease' })}
                    onClick={() => setUnits(line, line.units - 1)}
                  >
                    <i className="fas fa-minus" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon btn-sm btn-light me-1"
                    disabled={!linesEditable || busy}
                    title={intl.formatMessage({ id: 'quickSale.increase' })}
                    onClick={() => setUnits(line, line.units + 1)}
                  >
                    <i className="fas fa-plus" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon btn-sm btn-light-danger"
                    disabled={!linesEditable || busy}
                    title={intl.formatMessage({ id: 'quickSale.remove' })}
                    onClick={() => setUnits(line, 0)}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="d-flex gap-2 mb-8">
          <div className="flex-grow-1">
            <Select
              options={products}
              value={productToAdd}
              isDisabled={!linesEditable}
              onChange={(option) => setProductToAdd(option)}
              placeholder={intl.formatMessage({ id: 'quickSale.addExtra' })}
            />
          </div>
          <input
            type="number"
            min={1}
            className="form-control w-75px"
            value={unitsToAdd}
            disabled={!linesEditable}
            onChange={(event) =>
              setUnitsToAdd(Math.max(1, Number(event.target.value) || 1))
            }
          />
          <Button
            variant="light-primary"
            disabled={!linesEditable || !productToAdd || busy}
            onClick={() =>
              run(async () => {
                await setProduct(order.id, productToAdd.value, unitsToAdd);
                setProductToAdd(null);
                setUnitsToAdd(1);
              })
            }
          >
            {intl.formatMessage({ id: 'quickSale.add' })}
          </Button>
        </div>

        <h5 className="mb-3">
          {intl.formatMessage({ id: 'quickSale.deliveryAddress' })}
        </h5>
        <div className="d-flex gap-2 mb-8">
          <select
            className="form-select"
            value={addressId}
            disabled={!addressEditable}
            aria-label={intl.formatMessage({ id: 'quickSale.deliveryAddress' })}
            onChange={(event) => setAddressId(Number(event.target.value))}
          >
            <option value="">
              {intl.formatMessage({ id: 'quickSale.chooseAddress' })}
            </option>
            {addresses.map((address) => (
              <option key={address.id} value={address.id}>
                {formatAddress(address)}
              </option>
            ))}
          </select>
          <Button
            variant="light-primary"
            disabled={!addressEditable || !addressId || busy}
            onClick={() =>
              run(() => changeShippingAddress(order.id, Number(addressId)))
            }
          >
            {intl.formatMessage({ id: 'quickSale.save' })}
          </Button>
        </div>

        <h5 className="mb-3">
          {intl.formatMessage({ id: 'quickSale.carrier' })}
        </h5>
        <div className="d-flex gap-2 mb-8">
          <select
            className="form-select"
            value={carrierId}
            aria-label={intl.formatMessage({ id: 'quickSale.carrier' })}
            onChange={(event) => setCarrierId(Number(event.target.value))}
          >
            <option value="">-</option>
            {carriers.map((carrier) => (
              <option key={carrier.value} value={carrier.value}>
                {carrier.label}
              </option>
            ))}
          </select>
          <Button
            variant="light-primary"
            disabled={!carrierId || busy}
            onClick={() =>
              run(() => updateOrder(order.id, { shipping_service: carrierId }))
            }
          >
            {intl.formatMessage({ id: 'quickSale.save' })}
          </Button>
        </div>

        <h5 className="mb-3">
          {intl.formatMessage({ id: 'quickSale.deliveryDate' })}
        </h5>
        <div className="d-flex gap-2">
          <input
            type="date"
            className="form-control"
            value={promiseDate}
            onChange={(event) => setPromiseDate(event.target.value)}
          />
          <Button
            variant="light-primary"
            disabled={
              !promiseDate || promiseDate === order.promise_date || busy
            }
            onClick={() =>
              run(() => updateOrder(order.id, { promise_date: promiseDate }))
            }
          >
            {intl.formatMessage({ id: 'quickSale.save' })}
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          {intl.formatMessage({ id: 'quickSale.close' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { OrderEditModal };
